import type {
  AudioCatalog,
  VoiceTrackDefinition
} from "@/audio";
import {
  soundBubbleLevels,
  storySequenceLevels
} from "@/content/chinese";
import type { Difficulty } from "@/domain";

export type ChinesePlayerActivityAudioKind =
  | "sound-stimulus"
  | "story-narration";

export type ChinesePlayerVoiceTrackId =
  | `/audio/chinese/sound-bubbles/stimulus-${Difficulty}.ogg`
  | `/audio/chinese/story-sequence/narration-${Difficulty}.ogg`;

type ChinesePlayerVoiceMp3Path =
  | `/audio/chinese/sound-bubbles/stimulus-${Difficulty}.mp3`
  | `/audio/chinese/story-sequence/narration-${Difficulty}.mp3`;

export interface ChinesePlayerVoiceTrackDefinition {
  readonly audioKey: ChinesePlayerVoiceTrackId;
  readonly kind: ChinesePlayerActivityAudioKind;
  readonly levelId: string;
  readonly locale: "zh-CN";
  readonly track: VoiceTrackDefinition;
  readonly transcript: string;
}

function createVoiceTrack(
  audioKey: ChinesePlayerVoiceTrackId,
  mp3Path: ChinesePlayerVoiceMp3Path,
  transcript: string
): VoiceTrackDefinition {
  if (transcript.trim().length === 0) {
    throw new Error("Chinese player voice transcript cannot be empty");
  }

  return {
    sources: [
      { src: audioKey, type: "audio/ogg" },
      { src: mp3Path, type: "audio/mpeg" }
    ],
    gain: 1,
    captions: [
      {
        startMs: 0,
        endMs: 60_000,
        text: transcript
      }
    ],
    fallbackCaption: transcript
  };
}

function createSoundStimulusDefinition(
  level: (typeof soundBubbleLevels)[number]
): ChinesePlayerVoiceTrackDefinition {
  const audioKey =
    `/audio/chinese/sound-bubbles/stimulus-${level.difficulty}.ogg` as const;
  const mp3Path =
    `/audio/chinese/sound-bubbles/stimulus-${level.difficulty}.mp3` as const;
  const transcript = level.payload.spokenText;

  return {
    audioKey,
    kind: "sound-stimulus",
    levelId: level.id,
    locale: "zh-CN",
    track: createVoiceTrack(audioKey, mp3Path, transcript),
    transcript
  };
}

function createStoryNarrationDefinition(
  level: (typeof storySequenceLevels)[number]
): ChinesePlayerVoiceTrackDefinition {
  const audioKey =
    `/audio/chinese/story-sequence/narration-${level.difficulty}.ogg` as const;
  const mp3Path =
    `/audio/chinese/story-sequence/narration-${level.difficulty}.mp3` as const;
  const transcript = level.payload.narration;

  return {
    audioKey,
    kind: "story-narration",
    levelId: level.id,
    locale: "zh-CN",
    track: createVoiceTrack(audioKey, mp3Path, transcript),
    transcript
  };
}

export const b4ChinesePlayerVoiceTrackDefinitions: readonly ChinesePlayerVoiceTrackDefinition[] = [
  ...soundBubbleLevels.map(createSoundStimulusDefinition),
  ...storySequenceLevels.map(createStoryNarrationDefinition)
];

function createVoiceTrackMap(
  definitions: readonly ChinesePlayerVoiceTrackDefinition[]
): AudioCatalog["voice"] {
  const tracks: Record<string, VoiceTrackDefinition> = {};

  for (const definition of definitions) {
    if (Object.hasOwn(tracks, definition.audioKey)) {
      throw new Error(
        "Duplicate Chinese player voice track " + definition.audioKey
      );
    }
    tracks[definition.audioKey] = definition.track;
  }

  return tracks;
}

export const b4ChinesePlayerVoiceTracks = createVoiceTrackMap(
  b4ChinesePlayerVoiceTrackDefinitions
);

export function getChinesePlayerActivityVoiceTrack(
  levelId: string
): ChinesePlayerVoiceTrackDefinition | undefined {
  return b4ChinesePlayerVoiceTrackDefinitions.find(
    (definition) => definition.levelId === levelId
  );
}
